import { isMacAppStore } from '@/lib/appDistribution';
// Client-side rules for offering auto top-up (R-aug-11).
//
// Who may be asked is decided on the server and arrives as
// `user.autoTopup.offerEligible` (see backend/utils/auto_topup_eligibility.js).
// This module only adds what the server cannot see — the platform and checkout
// provider the user is on — and the arithmetic the opt-in copy needs.
//
// The rule the whole feature rests on: auto top-up is never on unless the user
// turned it on. Every helper here decides whether we may *ask*.

import { Platform } from 'react-native';

export const AUTO_TOPUP_DEFAULT_THRESHOLD_CREDITS = 250;
export const AUTO_TOPUP_DEFAULT_MONTHLY_LIMIT = 50;
export const AUTO_TOPUP_MIN_THRESHOLD_CREDITS = 100;
export const AUTO_TOPUP_MAX_THRESHOLD_CREDITS = 1000000;
export const AUTO_TOPUP_BONUS_PERCENT = 10;

// Suggested triggers for one-click opt-in. The settings API also accepts any
// whole-credit threshold inside the min/max above.
export const AUTO_TOPUP_THRESHOLD_OPTIONS = [100, 250, 500, 1000];

export function bonusCreditsFor(credits) {
  const amount = Number(credits);
  if (!Number.isSafeInteger(amount) || amount <= 0) return 0;
  return Math.floor(amount * AUTO_TOPUP_BONUS_PERCENT / 100);
}

export function grantedCreditsFor(credits) {
  const amount = Number(credits);
  if (!Number.isSafeInteger(amount) || amount <= 0) return 0;
  return amount + bonusCreditsFor(amount);
}

// Automatic card billing is offered on the web. Native credit purchases use
// their existing app-store billing flows.

/**
 * Whether an auto top-up opt-in may be shown to this user right now.
 *
 * `offerEligible` already covers Plus, a saved card, no dispute hold, and a
 * 30-day cooldown after any decline. We re-check
 * `enabled` locally because the store merges settings updates, so a user who
 * just turned it on should stop seeing the offer without waiting for a refetch.
 *
 * Whop offers require the server's separate rollout gate and saved-method
 * eligibility; a Whop subscription alone is not a payment authorization.
 */
export function canOfferAutoTopup({ user, usesWhopCheckout, platform = Platform.OS } = {}) {
  if (!user || platform !== 'web') return false;
  if (usesWhopCheckout && !user.autoTopup?.whopAvailable) return false;
  if (user.autoTopup?.enabled) return false;
  return user.autoTopup?.offerEligible === true;
}

/**
 * Whether a contextual auto top-up nudge (out-of-credits, low balance, credit
 * store) may appear. Broader than `canOfferAutoTopup`: these surfaces only
 * open the configuration screen, which itself handles a missing saved card,
 * so they do not need the server's repeat-buyer eligibility. Auto top-up is a
 * Plus feature managed on web and macOS.
 */
export function canNudgeAutoTopup({ user, usesWhopCheckout, platform = Platform.OS } = {}) {
  if (!user?.flags?.isPlus) return false;
  if (platform !== 'web' && platform !== 'macos') return false;
  if (platform === 'macos' && isMacAppStore()) return false;
  if (user.autoTopup?.enabled) return false;
  if (usesWhopCheckout && !user.autoTopup?.whopAvailable) return false;
  return true;
}

/**
 * The smallest monthly cap that can still cover one pack. Mirrors
 * `minMonthlySpendLimitForPack` on the server, which rejects anything lower —
 * a cap under the pack price means auto top-up can never actually fire.
 */
export function minMonthlyLimitForPackPrice(packPrice) {
  const price = Number(packPrice);
  if (!Number.isFinite(price) || price <= 0) return null;
  return Math.ceil(price);
}

/** Mirrors the server check that rejects a cap a pack could never fit under. */
export function isMonthlyLimitEnoughForPack(monthlySpendLimit, packPrice) {
  // null is "no limit", which always covers a pack.
  if (monthlySpendLimit === null || monthlySpendLimit === undefined) return true;
  const min = minMonthlyLimitForPackPrice(packPrice);
  if (min === null) return true;
  const limit = Number(monthlySpendLimit);
  if (!Number.isFinite(limit)) return false;
  return limit >= min;
}

/**
 * What we pre-fill as the monthly cap for a one-click opt-in. Keeps the
 * familiar $50 default unless the pack costs more, in which case the cap has to
 * rise to the pack price or the feature would be dead on arrival.
 */
export function suggestedMonthlyLimitForPackPrice(packPrice) {
  const min = minMonthlyLimitForPackPrice(packPrice);
  if (min === null) return AUTO_TOPUP_DEFAULT_MONTHLY_LIMIT;
  return Math.max(AUTO_TOPUP_DEFAULT_MONTHLY_LIMIT, min);
}

/**
 * Whether one top-up of this pack actually lifts the balance past the trigger.
 * If it does not, the balance is still under the trigger when the charge lands
 * and the worker buys again on its next pass — minutes later, then daily until
 * the balance catches up. Mirrors `packClearsThreshold` on the server, which
 * rejects the pairing.
 */
export function packClearsThreshold(packCredits, thresholdCredits) {
  const credits = Number(packCredits);
  const threshold = Number(thresholdCredits);
  if (!Number.isFinite(credits) || !Number.isFinite(threshold)) return false;
  // Neither half can be missing or nonsensical. Guarded explicitly because
  // Number(null) is 0, which would otherwise read as a threshold any pack
  // clears.
  if (credits <= 0 || threshold <= 0) return false;
  return credits > threshold;
}

/** The highest trigger this pack can clear in one charge, or null if none can. */
export function largestThresholdForPack(packCredits) {
  const granted = grantedCreditsFor(packCredits);
  const usable = AUTO_TOPUP_THRESHOLD_OPTIONS.filter(
    (option) => packClearsThreshold(granted, option),
  );
  return usable.length ? Math.max(...usable) : null;
}

/**
 * The trigger to pre-fill for a one-click opt-in on this pack: the usual
 * default when the pack can clear it, otherwise the highest trigger it can.
 * null means this pack cannot be used for auto top-up at all.
 */
export function thresholdForOneClickOptIn(packCredits) {
  if (packClearsThreshold(packCredits, AUTO_TOPUP_DEFAULT_THRESHOLD_CREDITS)) {
    return AUTO_TOPUP_DEFAULT_THRESHOLD_CREDITS;
  }
  return largestThresholdForPack(packCredits);
}

/**
 * Whether the one-click opt-in may be offered alongside a purchase of `pack`.
 * Beyond being allowed to ask at all, the pack has to be one auto top-up can
 * actually work with — otherwise ticking the box would buy a second charge.
 */
export function canOfferAutoTopupForPack({ user, usesWhopCheckout, platform, pack } = {}) {
  if (!canOfferAutoTopup({ user, usesWhopCheckout, platform })) return false;
  return thresholdForOneClickOptIn(pack?.credits) !== null;
}

/** Settings for enabling auto top-up on the pack the user just bought. */
export function autoTopupSettingsForPack(pack, { thresholdCredits, version = 0, provider = 'valmar', paymentMethodFingerprint } = {}) {
  if (!pack?.id) return null;
  const threshold = thresholdCredits || thresholdForOneClickOptIn(pack.credits);
  if (threshold === null) return null;
  return {
    enabled: true,
    creditPackId: pack.id,
    thresholdCredits: threshold,
    monthlySpendLimit: suggestedMonthlyLimitForPackPrice(pack.price),
    version, provider, paymentMethodFingerprint, consent: true,
    quote: { creditPackId: pack.id, credits: pack.credits, priceCents: Math.round(pack.price * 100), currency: (pack.currency || 'USD').toUpperCase() },
  };
}
